import Icon from "@components/library/Icon/Icon";
import { COLORS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import i18n from "@locales";
import { RootState } from "@redux/store";
import { IconName } from "@tsTypes/icons";
import { RequestForStartupsShowResponse } from "@tsTypes/request_for_startups/show";
import { t } from "@utils/i18n";
import { ReactElement } from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";

interface Props {
  request: RequestForStartupsShowResponse;
}

type DataPoint = {
  key: string;
  iconName: IconName;
  content: string | ReactElement | null;
};

const i18nPrefix = "pages.request_for_startups_page.data_points";

export default function DataPoints({ request }: Props) {
  const isMobile = useSelector((state: RootState) => state.window.isMobile);

  const dataPoints: DataPoint[] = [];

  if (request.requestable.participation_type) {
    dataPoints.push({
      key: "participation_type",
      iconName: "Location",
      content: t([i18nPrefix, "participation_type", request.requestable.participation_type]),
    });
  }

  if (request.requestable.max_accepted) {
    dataPoints.push({
      key: "max_accepted",
      iconName: "Organization",
      content: t([i18nPrefix, "max_accepted"], { count: request.requestable.max_accepted }),
    });
  }

  if (request.requestable.duration_in_weeks) {
    let i18nKey = "duration.weeks";
    let count = request.requestable.duration_in_weeks;

    if (request.requestable.duration_in_weeks % 4 === 0) {
      i18nKey = "duration.months";
      count = request.requestable.duration_in_weeks / 4;
    }

    dataPoints.push({
      key: "duration",
      iconName: "History",
      content: t([i18nPrefix, i18nKey], { count }),
    });
  }

  if (typeof request.requestable.funding_amount_usd === "number") {
    let content = "";

    if (request.requestable.funding_amount_usd === 0) {
      content = t([i18nPrefix, "no_funding"]);
    } else {
      content = t([i18nPrefix, "funding"], {
        count: i18n.numberToCurrency(request.requestable.funding_amount_usd, { precision: 0 }),
      });
    }

    dataPoints.push({ key: "funding", iconName: "Funding", content });
  }

  if (request.requestable.alumni_count) {
    dataPoints.push({
      key: "alumni",
      iconName: "Organization",
      content: t([i18nPrefix, "alumni"], { count: request.requestable.alumni_count }),
    });
  }

  if (request.requestable.equity_percentage) {
    dataPoints.push({
      key: "equity",
      iconName: "Funding",
      content: t([i18nPrefix, "equity"], { equity: request.requestable.equity_percentage }),
    });
  }

  const miscDataPoints: DataPoint[] = [
    { key: "misc_time", iconName: "History", content: request.requestable.misc_time },
    { key: "misc_building", iconName: "Organization", content: request.requestable.misc_building },
    { key: "misc_person", iconName: "Employees", content: request.requestable.misc_person },
    { key: "misc_rocket", iconName: "Accelerator", content: request.requestable.misc_rocket },
    { key: "misc_dollar", iconName: "Funding", content: request.requestable.misc_dollar },
  ];

  miscDataPoints.forEach(({ key, iconName, content }) => {
    if (content) {
      dataPoints.push({ key, iconName, content });
    }
  });

  return (
    <Container count={dataPoints.length}>
      {dataPoints.map(({ key, iconName, content }) => (
        <DataPoint key={"dataPoint-" + key}>
          <Icon
            name={iconName}
            size="md"
            color={isMobile ? COLORS.NEUTRAL_400 : COLORS.BLUE_LIGHT_600}
          />
          {content}
        </DataPoint>
      ))}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  margin: 40px 0;
  gap: 96px;

  ${WINDOW_DIMENSIONS.TABLET_MEDIA_QUERY} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 12px;
  }

  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    grid-template-columns: 1fr;
    border: 1px solid ${COLORS.NEUTRAL_200};
    border-radius: 8px;
    padding: 16px;
  }
`;

const DataPoint = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLORS.BLUE_900};
  ${FONTS.MEDIUM_2}

  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    color: ${COLORS.BLACK};
  }
`;
